import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

import type { Orders_ProductionQueueResponseDados } from '../contracts/Orders_ProductionQueueResponse';
import type { Orders_ScanProductionStepResponseDados } from '../contracts/Orders_ScanProductionStepResponse';
import type { Orders_ProductionHistoryResponseDados } from '../contracts/Orders_ProductionHistoryResponse';

import '../contracts/Orders_ProductionHistoryResponse';
import '../contracts/Orders_ProductionQueueResponse';
import '../contracts/Orders_ScanProductionStepInput';
import '../contracts/Orders_ScanProductionStepResponse';
/**
 * Composable for the production scanner page.
 * Handles QR code double-scan for production step tracking,
 * production queue fetching, and production history.
 */
export function useSOScanner() {
  const isLoading = ref(false);
  const isScanning = ref(false);
  const error = ref(null);
  const queueItems = ref<Orders_ProductionQueueResponseDados>([]);
  const scanResult = ref<Orders_ScanProductionStepResponseDados | { action: string; message?: string } | null>(null);
  const historyItems = ref<Orders_ProductionHistoryResponseDados>([]);
  const stationQrcode = ref('');
  const basePath = '/index.php/so/orders';

  const fetchProductionQueue = async () => {
    if (isLoading.value) return queueItems.value;
    log('useSOScanner', 'fetchProductionQueue', 'Fetching production queue');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/production_queue`, { responseContract: 'Orders_ProductionQueueResponse' });
      if (response.success) {
        queueItems.value = response.dados || [];
        return queueItems.value;
      }
      throw new Error(response.error || 'Erro ao buscar fila de produção');
    } catch (err) {
      logError('useSOScanner', 'fetchProductionQueue', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const scanStep = async (stationQr: string, itemQr: string, force: boolean = false) => {
    log('useSOScanner', 'scanStep', 'Scanning', { stationQr, itemQr, force });
    isScanning.value = true;
    error.value = null;
    scanResult.value = null;
    try {
      const response = await httpService.post(`${basePath}/scan_production_step`, {
        station_qrcode: stationQr,
        item_qrcode: itemQr,
        force
      }, { inputContract: 'Orders_ScanProductionStepInput', responseContract: 'Orders_ScanProductionStepResponse' });

      if (response.success) {
        scanResult.value = response.dados ?? null;
        return response.dados;
      }

      scanResult.value = { action: 'error', message: response.error ?? undefined };
      throw new Error(response.error || 'Erro ao escanear');
    } catch (err) {
      logError('useSOScanner', 'scanStep', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isScanning.value = false;
    }
  };

  const fetchProductionHistory = async (orderItemId: number) => {
    if (isLoading.value) return historyItems.value;
    log('useSOScanner', 'fetchProductionHistory', 'Fetching history', { orderItemId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/production_history/${orderItemId}`, { responseContract: 'Orders_ProductionHistoryResponse' });
      if (response.success) {
        historyItems.value = response.dados || [];
        return historyItems.value;
      }
      throw new Error(response.error || 'Erro ao buscar histórico de produção');
    } catch (err) {
      logError('useSOScanner', 'fetchProductionHistory', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const clearScanResult = () => {
    scanResult.value = null;
  };

  const clearStation = () => {
    stationQrcode.value = '';
  };

  return {
    isLoading,
    isScanning,
    error,
    queueItems,
    scanResult,
    historyItems,
    stationQrcode,
    fetchProductionQueue,
    scanStep,
    fetchProductionHistory,
    clearScanResult,
    clearStation
  };
}
